import { ChevronRight, Search } from 'lucide-react'
import { Card } from '@/components/ui'
import { formatShmpCourse } from '@/lib/shmpCourse'
import { formatCurrency } from '@/lib/utils'
import type { ShmpDashboardSummary, ShmpPartnerMetrics } from '@/types'

export type ShmpSortKey = 'revenue_fact' | 'plan_completion_pct' | 'leads_fact' | 'sales_fact'

function number(value: number): string {
  return Math.round(value || 0).toLocaleString('ru-RU')
}

function percent(value: number | null): string {
  return value === null ? '—' : `${value.toLocaleString('ru-RU')}%`
}

function tone(value: number | null): string {
  if (value === null) return 'text-muted-foreground'
  if (value >= 100) return 'text-success'
  if (value >= 80) return 'text-amber-500'
  return 'text-error'
}

export function ShmpPartnersTable(props: {
  partners: ShmpPartnerMetrics[]
  summary: ShmpDashboardSummary
  search: string
  onSearchChange: (value: string) => void
  sortKey: ShmpSortKey
  onSortChange: (key: ShmpSortKey) => void
  onOpenPartner: (partnerNumber: number) => void
}) {
  const sortHeader = (key: ShmpSortKey, label: string) => (
    <button className="whitespace-nowrap hover:text-foreground" onClick={() => props.onSortChange(key)}>
      {label}{props.sortKey === key ? ' ↓' : ''}
    </button>
  )

  return (
    <Card className="overflow-hidden border border-border/70 p-0 shadow-none">
      <div className="flex flex-col gap-3 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="font-semibold">Франчайзи</div>
        <label className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={props.search}
            onChange={event => props.onSearchChange(event.target.value)}
            placeholder="ФИО, город или номер"
            className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-xs sm:w-64"
          />
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1280px] text-xs">
          <thead className="bg-muted/45 text-left text-[10px] text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">Франчайзи</th>
              <th className="py-3 font-medium">Курс</th>
              <th className="py-3 font-medium">{sortHeader('revenue_fact', 'Выручка')}</th>
              <th className="py-3 font-medium">План / факт</th>
              <th className="py-3 font-medium">{sortHeader('plan_completion_pct', '% плана')}</th>
              <th className="py-3 font-medium">{sortHeader('leads_fact', 'Заявки')}</th>
              <th className="py-3 font-medium">Записи (CR1)</th>
              <th className="py-3 font-medium">Приходы (CR2)</th>
              <th className="py-3 font-medium">{sortHeader('sales_fact', 'Продажи (CR3)')}</th>
              <th className="py-3 font-medium">Средний чек</th>
              <th className="py-3 font-medium">Возвраты</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {props.partners.map(row => (
              <tr
                key={row.partner_number}
                className="cursor-pointer border-t border-border/60 hover:bg-muted/30"
                onClick={() => props.onOpenPartner(row.partner_number)}
              >
                <td className="px-4 py-3">
                  <div className="font-semibold">{row.persons_fio || `Франчайзи №${row.partner_number}`}</div>
                  <div className="mt-0.5 text-[10px] text-muted-foreground">
                    {row.cities_text || 'Город не указан'} · №{row.partner_number} · {row.studio_count} студ.
                  </div>
                </td>
                <td>
                  <span className="whitespace-nowrap rounded-md border border-primary/40 bg-primary/10 px-2 py-1">
                    {formatShmpCourse(row.course_numbers, row.course_number)}
                  </span>
                </td>
                <td className="font-semibold">{formatCurrency(row.revenue_fact)}</td>
                <td><span className="text-muted-foreground">{formatCurrency(row.revenue_plan)}</span> / {formatCurrency(row.revenue_fact)}</td>
                <td className={`font-semibold ${tone(row.plan_completion_pct)}`}>{percent(row.plan_completion_pct)}</td>
                <td>{number(row.leads_fact)}</td>
                <td>{number(row.appointments_fact)} <span className="text-[10px] text-muted-foreground">({percent(row.cr1)})</span></td>
                <td>{number(row.visitors_fact)} <span className="text-[10px] text-muted-foreground">({percent(row.cr2)})</span></td>
                <td>{number(row.sales_fact)} <span className="text-[10px] text-muted-foreground">({percent(row.cr3)})</span></td>
                <td>{row.average_check === null ? '—' : formatCurrency(row.average_check)}</td>
                <td>{formatCurrency(row.refunds_fact)}</td>
                <td><ChevronRight size={16} className="text-muted-foreground" /></td>
              </tr>
            ))}
          </tbody>
          {props.partners.length > 0 && (
            <tfoot className="border-t border-border bg-muted/20 font-semibold">
              <tr>
                <td className="px-4 py-3">Всего</td>
                <td>—</td>
                <td>{formatCurrency(props.summary.revenue_fact)}</td>
                <td>{formatCurrency(props.summary.revenue_plan)} / {formatCurrency(props.summary.revenue_fact)}</td>
                <td className={tone(props.summary.plan_completion_pct)}>{percent(props.summary.plan_completion_pct)}</td>
                <td>{number(props.summary.leads_fact)}</td>
                <td>{number(props.summary.appointments_fact)} <span className="text-[10px] text-muted-foreground">({percent(props.summary.cr1)})</span></td>
                <td>{number(props.summary.visitors_fact)} <span className="text-[10px] text-muted-foreground">({percent(props.summary.cr2)})</span></td>
                <td>{number(props.summary.sales_fact)} <span className="text-[10px] text-muted-foreground">({percent(props.summary.cr3)})</span></td>
                <td>{props.summary.average_check === null ? '—' : formatCurrency(props.summary.average_check)}</td>
                <td>{formatCurrency(props.summary.refunds_fact)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {props.partners.length === 0 && (
        <div className="px-4 py-10 text-center text-sm text-muted-foreground">Нет данных за выбранный диапазон.</div>
      )}
    </Card>
  )
}
